// B-360 · UNA CLÍNICA CON UN PAGO PENDIENTE CON BIANGO (backend/services/impago.js).
//
// Tres fases, contadas desde el día que Biango apunta en Superadmin («Impago desde»):
//   · del día 1 al 7: un aviso a la administración de la clínica;
//   · del día 8 al 15: el aviso a todo el equipo (el marketing y WhatsApp están parados);
//   · desde el día 16: la cuenta en pausa: solo descargar los datos y hablar con soporte. No se borra nada.
//
// Pregunta a /auth/impago al entrar y cada cinco minutos, y se entera EN EL ACTO si el servidor contesta 402 a cualquier
// otra petición: services/api.js lanza el evento `biango:impago` con lo que dijo el servidor.
//
// La pantalla de pausa va POR DEBAJO del chat (zIndex 1500 < 2000 del ChatWidget): «Escribir a soporte» abre el chat por
// encima de ella, que es justo lo que la clínica tiene que poder hacer.
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useEsAdminDeLaClinica } from '../services/permisosDelServidor'
import ExportacionCompleta from './ExportacionCompleta'
import DescargarMisNotas from './notasSesion/DescargarMisNotas'   // t616 · B-2476 · lo que cada profesional se lleva: no va en la exportación de la clínica
import AvisoEspacioLleno from './AvisoEspacioLleno'   // t120 · el espacio de la clínica lleno (413 ESPACIO_LLENO)
import AvisoCasoSinContestar from './AvisoCasoSinContestar'   // B-722 (t413) · el último aviso del caso referido
import AvisoEdicionLocal from './AvisoEdicionLocal'   // B-176 · la licencia de la edición local (en la nube no pinta nada)
import AvisoContratoPruebaPrevia from './AvisoContratoPruebaPrevia'   // t626 · B-2529 · su contrato con prueba previa, pendiente de aprobar la prueba
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const fechaCorta = (ymd) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) return ''
  const [a, m, d] = String(ymd).split('-')
  return `${d}/${m}/${a}`
}
const CLAVE_CERRADO = 'biango:impago-visto'

// t120 (6-oct-2026) · LOS AVISOS DE LA CUENTA CON BIANGO, JUNTOS: el de un pago pendiente (aquí abajo) y el del espacio de la
// clínica lleno (components/AvisoEspacioLleno, que sale cuando una subida contesta 413 ESPACIO_LLENO). Layout monta este
// componente en sus dos diseños (el normal y el modo doctor), así que el del espacio sale en los dos sin tocar Layout.
// t199 · Y EL DE UNA CLÍNICA DADA DE BAJA (backend/services/bajaClinica.js) va DENTRO del de impago (AvisoDeImpago) y manda
// sobre él: si la clínica está de baja, sale ese y no el otro.
// B-722 (t413) · y el último aviso del caso referido sin contestar (components/AvisoCasoSinContestar), por el mismo motivo:
// así sale en los dos diseños de Layout sin tocarlo.
export default function AvisoImpago() {
  return (
    <>
      <AvisoDeImpago />
      <AvisoEspacioLleno />
      <AvisoCasoSinContestar />
      <AvisoEdicionLocal />
      <AvisoContratoPruebaPrevia />
    </>
  )
}

// La copia rápida de los datos (un JSON), la misma en la pausa por impago y en la baja. Lanza si no se puede.
async function bajarCopiaRapida() {
  const r = await api.get('/portabilidad/export', { responseType: 'blob' })
  const url = URL.createObjectURL(r.data)
  const a = document.createElement('a')
  a.href = url
  a.download = 'datos-de-la-clinica.json'
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
}

// ¿Está la clínica de la sesión dada de baja y todavía en sus 30 días? Pregunta a /auth/baja al entrar y cada cinco minutos,
// y se entera EN EL ACTO si el servidor contesta 402 `BAJA` a cualquier otra petición (services/api.js lanza `biango:baja`).
// Pasados los 30 días no hace falta: el servidor cierra la sesión (403 `BAJA_CERRADA`) y la pantalla de entrar lo dice.
function useBajaDeLaClinica() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['baja-clinica'],
    queryFn: () => api.get('/auth/baja').then((r) => r.data).catch(() => null),
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000,
  })
  useEffect(() => {
    const h = (e) => {
      const est = (e && e.detail) || null
      if (est && est.deBaja) qc.setQueryData(['baja-clinica'], (antes) => (antes && antes.deBaja ? antes : est))
    }
    window.addEventListener('biango:baja', h)
    return () => window.removeEventListener('biango:baja', h)
  }, [qc])
  return data && data.deBaja === true && data.cerrada !== true ? data : null
}

function AvisoDeBaja({ estado, esAdmin }) {
  const [descargando, setDescargando] = useState(false)
  const [errorDescarga, setErrorDescarga] = useState('')
  const [verCompleta, setVerCompleta] = useState(false)
  const abrirSoporte = () => window.dispatchEvent(new CustomEvent('abrir-ayuda'))
  const descargar = async () => {
    setDescargando(true); setErrorDescarga('')
    try { await bajarCopiaRapida() }
    catch (_) { setErrorDescarga(t('components.AvisoImpago.no_se_han_podido_descargar', 'No se han podido descargar ahora. Vuelve a intentarlo o escribe a soporte.')) }
    finally { setDescargando(false) }
  }
  const hasta = estado.hasta ? t('components.AvisoImpago.el', 'el {{hasta}}{{x}}', { hasta: estado.hasta, x: estado.hasta_hora ? t('components.AvisoImpago.a_las', ' a las {{hasta_hora}}', { hasta_hora: estado.hasta_hora }) : '' }) : ''

  // El equipo de Biango dentro de la clínica: a él no le corta nada (su camino no pasa por la puerta). Una franja arriba,
  // para que sepa en qué estado está la clínica que mira.
  if (estado.equipo_biango === true) {
    return (
      <div role="status" data-baja-franja="" style={{ position: 'fixed', left: '16px', right: '16px', top: 'calc(10px + var(--sa-arriba))', zIndex: 1500, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ pointerEvents: 'auto', maxWidth: '760px', width: '100%', background: 'var(--surface)', border: '1px solid color-mix(in srgb, var(--danger) 55%, var(--border))', borderRadius: 'var(--radius-lg)', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '10px 14px', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--text-primary)' }}>
          <b>{tx('components.AvisoImpago.clinica_dada_de_baja', 'Clínica dada de baja{{x}}.', { x: estado.desde ? t('components.AvisoImpago.desde_el', ' desde el {{desde}}', { desde: estado.desde }) : '' })}</b>{tx('components.AvisoImpago.tu_entras_como_equipo_de', ' {{x}} Tú entras como equipo de Biango.', { x: hasta ? t('components.AvisoImpago.su_equipo_solo_puede_descargar', 'Su equipo solo puede descargar sus datos hasta {{hasta}}.', { hasta }) : t('components.AvisoImpago.su_equipo_solo_puede_descargar_2', 'Su equipo solo puede descargar sus datos.') })}
        </div>
      </div>
    )
  }

  // La clínica: arriba, el aviso con sus fechas y el botón de descargar; lo demás de la aplicación está parado.
  return (
    <div className="modal-safe" role="alertdialog" aria-labelledby="baja-titulo" data-baja-pausa=""
      style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '16px', overflowY: 'auto' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 20px 50px rgba(0,0,0,0.18)', width: '100%', maxWidth: '560px', padding: '24px', marginTop: 'min(calc(8vh / var(--z, 1)), 64px)' }}>
        <div id="baja-titulo" style={{ fontSize: '19px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>{t('components.AvisoImpago.tu_clinica_esta_dada_de', 'Tu clínica está dada de baja')}</div>
        <div style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'var(--text-secondary)' }}>
          {estado.mensaje}
        </div>
        <div style={{ fontSize: '13.5px', lineHeight: 1.55, color: 'var(--text-secondary)', marginTop: '8px' }}>
          {t('components.AvisoImpago.mientras_tanto_solo_se_pueden', 'Mientras tanto solo se pueden descargar los datos y hablar con soporte.')}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '18px' }}>
          {esAdmin && (
            <button type="button" className="btn btn-primary" onClick={descargar} disabled={descargando}>
              {descargando ? t('components.AvisoImpago.descargando', 'Descargando…') : t('components.AvisoImpago.descargar_los_datos', 'Descargar los datos')}
            </button>
          )}
          {esAdmin && (
            <button type="button" className="btn btn-secondary" onClick={() => setVerCompleta((v) => !v)}>
              {verCompleta ? t('components.AvisoImpago.ocultar_la_exportacion_completa', 'Ocultar la exportación completa') : t('components.AvisoImpago.exportacion_completa_con_archivos', 'Exportación completa (con archivos)')}
            </button>
          )}
          <button type="button" className={esAdmin ? 'btn btn-secondary' : 'btn btn-primary'} onClick={abrirSoporte}>{t('components.AvisoImpago.escribir_a_soporte', 'Escribir a soporte')}</button>
        </div>
        {esAdmin && verCompleta && (
          <div style={{ marginTop: '16px', maxHeight: 'calc(50vh / var(--z, 1))', overflowY: 'auto', borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
            <ExportacionCompleta />
          </div>
        )}
        {!esAdmin && (
          <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '10px' }}>{t('components.AvisoImpago.la_descarga_de_los_datos', 'La descarga de los datos la hace la administración de la clínica.')}</div>
        )}
        {errorDescarga && <div style={{ fontSize: '12.5px', color: 'var(--danger)', marginTop: '10px' }}>{errorDescarga}</div>}
        <DescargarMisNotas />
      </div>
    </div>
  )
}

function AvisoDeImpago() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { esAdmin } = useEsAdminDeLaClinica({ habilitada: !!user })
  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['impago'],
    queryFn: () => api.get('/auth/impago').then((r) => r.data).catch(() => null),
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000,
  })
  // Lo que dijo el servidor en un 402. Solo manda si es MÁS RECIENTE que la última pregunta y de una fase MAYOR (la
  // pregunta de cada cinco minutos puede ir por detrás); y entonces se vuelve a preguntar UNA vez. En la pausa, cada
  // pantalla que siga preguntando por debajo recibe su 402: con la misma fase no se repite la pregunta, y al cobrar,
  // la respuesta nueva (al día) es más reciente que cualquier 402 viejo y la pausa se quita sola.
  const [delServidor, setDelServidor] = useState(null)
  const faseConocida = Number((data && data.fase) || 0)
  useEffect(() => {
    const h = (e) => {
      const estado = (e && e.detail) || null
      setDelServidor({ estado, at: Date.now() })
      if (estado && Number(estado.fase) > faseConocida) qc.invalidateQueries({ queryKey: ['impago'] })
    }
    window.addEventListener('biango:impago', h)
    return () => window.removeEventListener('biango:impago', h)
  }, [qc, faseConocida])
  const [visto, setVisto] = useState(() => {
    try { return sessionStorage.getItem(CLAVE_CERRADO) || '' } catch { return '' }
  })
  const [descargando, setDescargando] = useState(false)
  const [errorDescarga, setErrorDescarga] = useState('')
  // Con la cuenta en pausa la clínica solo puede llevarse sus datos: la copia rápida (un JSON) o la COMPLETA (todo, con sus
  // archivos, en ZIP), que es la que de verdad devuelve los datos. Se abre aquí mismo: con la pausa no se llega a Ajustes.
  const [verCompleta, setVerCompleta] = useState(false)
  // t199 · una clínica dada de baja: su aviso manda sobre este (después de todos los hooks, para no cambiar su orden).
  const baja = useBajaDeLaClinica()

  const usarEvento = delServidor && delServidor.estado && delServidor.at > (dataUpdatedAt || 0)
    && Number(delServidor.estado.fase) > faseConocida
  const est = usarEvento ? delServidor.estado : data
  const fase = Number((est && est.fase) || 0)
  if (baja) return <AvisoDeBaja estado={baja} esAdmin={esAdmin} />
  if (!fase) return null
  // Del día 1 al 7, el aviso es para la administración.
  if (fase === 1 && !esAdmin) return null

  const desde = fechaCorta(est.desde)
  const paraWhatsapp = fechaCorta(est.sin_marketing_ni_whatsapp_desde)
  const paraPausa = fechaCorta(est.solo_datos_desde)
  const abrirSoporte = () => window.dispatchEvent(new CustomEvent('abrir-ayuda'))

  const descargar = async () => {
    setDescargando(true); setErrorDescarga('')
    try {
      await bajarCopiaRapida()
    } catch (_) {
      setErrorDescarga(t('components.AvisoImpago.no_se_han_podido_descargar', 'No se han podido descargar ahora. Vuelve a intentarlo o escribe a soporte.'))
    } finally { setDescargando(false) }
  }

  if (fase >= 3) {
    return (
      <div className="modal-safe" role="alertdialog" aria-labelledby="impago-titulo"
        style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 20px 50px rgba(0,0,0,0.18)', width: '100%', maxWidth: '520px', padding: '24px' }}>
          <div id="impago-titulo" style={{ fontSize: '19px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>{t('components.AvisoImpago.la_cuenta_esta_en_pausa', 'La cuenta está en pausa')}</div>
          <div style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'var(--text-secondary)' }}>
            {tx('components.AvisoImpago.hay_un_pago_pendiente_con', 'Hay un pago pendiente con Biango desde el {{desde}}. Mientras no se regularice, solo se pueden descargar los datos y hablar con soporte.', { desde })}
            <b>{t('components.AvisoImpago.no_se_ha_borrado_nada', ' No se ha borrado nada')}</b>{t('components.AvisoImpago.en_cuanto_se_arregle_todo', ': en cuanto se arregle, todo vuelve a estar como lo dejasteis.')}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '18px' }}>
            <button type="button" className="btn btn-primary" onClick={abrirSoporte}>{t('components.AvisoImpago.escribir_a_soporte', 'Escribir a soporte')}</button>
            {esAdmin && (
              <button type="button" className="btn btn-secondary" onClick={descargar} disabled={descargando}>
                {descargando ? t('components.AvisoImpago.descargando', 'Descargando…') : t('components.AvisoImpago.descargar_los_datos', 'Descargar los datos')}
              </button>
            )}
            {esAdmin && (
              <button type="button" className="btn btn-secondary" onClick={() => setVerCompleta((v) => !v)}>
                {verCompleta ? t('components.AvisoImpago.ocultar_la_exportacion_completa', 'Ocultar la exportación completa') : t('components.AvisoImpago.exportacion_completa_con_archivos', 'Exportación completa (con archivos)')}
              </button>
            )}
          </div>
          {esAdmin && verCompleta && (
            <div style={{ marginTop: '16px', maxHeight: 'calc(50vh / var(--z, 1))', overflowY: 'auto', borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
              <ExportacionCompleta />
            </div>
          )}
          {!esAdmin && (
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '10px' }}>{t('components.AvisoImpago.la_descarga_de_los_datos', 'La descarga de los datos la hace la administración de la clínica.')}</div>
          )}
          {errorDescarga && <div style={{ fontSize: '12.5px', color: 'var(--danger)', marginTop: '10px' }}>{errorDescarga}</div>}
        <DescargarMisNotas />
        </div>
      </div>
    )
  }

  // Fases 1 y 2: una franja abajo, que se puede cerrar hasta la próxima vez que se entre (sessionStorage).
  const clave = fase + ':' + (est.desde || '')
  if (visto === clave) return null
  const cerrar = () => {
    setVisto(clave)
    try { sessionStorage.setItem(CLAVE_CERRADO, clave) } catch { /* sin almacenamiento: se cierra solo aquí */ }
  }
  const texto = t('components.AvisoImpago.plural_hay_un_pago_pendiente', { one: 'Hay un pago pendiente con Biango desde el {{desde}}. Si no se regulariza, el {{paraWhatsapp}} se pararán el marketing y WhatsApp, y el {{paraPausa}} la cuenta quedará en pausa (solo descargar los datos y soporte).', other: 'Hay un pago pendiente con Biango desde el {{desde}}: el marketing y WhatsApp están parados (lo que escriben los pacientes se sigue guardando). Si no se regulariza, el {{paraPausa}} la cuenta quedará en pausa.' }, { count: fase, _forma: (fase === 1) ? 'one' : 'other', desde, paraWhatsapp, paraPausa })
  return (
    <div role="status" style={{ position: 'fixed', left: '16px', right: '16px', bottom: 'calc(16px + var(--sa-abajo))', zIndex: 1500, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }} data-aviso-impago="">
      <div style={{ pointerEvents: 'auto', maxWidth: '760px', width: '100%', background: 'var(--surface)', border: '1px solid color-mix(in srgb, var(--warning) 55%, var(--border))', borderRadius: 'var(--radius-lg)', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--text-primary)' }}>{texto}</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" className="btn btn-primary" onClick={abrirSoporte}>{t('components.AvisoImpago.escribir_a_soporte', 'Escribir a soporte')}</button>
          <button type="button" className="btn btn-secondary" onClick={cerrar}>{t('components.AvisoImpago.entendido', 'Entendido')}</button>
        </div>
      </div>
    </div>
  )
}
